import PropTypes from 'prop-types';
import { useMemo } from 'react';
import FJAIVideoGeneratorData, {
    AI_GENERATOR_VIDEO_TAB_TYPE,
    AI_VIDEO_GENERATION_MODEL_LIST,
    IMAGE_TO_VIDEO_DROPDOWN_LIST,
    TEXT_TO_VIDEO_DROPDOWN_LIST,
    IMAGE_UPLOAD_MODES,
    MODEL_VALUE_MAP,
    IMAGE_TO_VIDEO_MODEL_FILTERS,
    TEXT_TO_VIDEO_MODEL_FILTERS,
} from '../../../../config/dataConfig/FJAIVideoGeneratorData.js';
import FJLocalStore from '../../../../store/FJLocalStore.js';
import FJAppLeft from '../../../FJMainPreview/FJAppLeft/FJAppLeft.jsx';
import FJVerticalScroller from '../../../../common/components/FJScroller/FJVerticalScroller.jsx';
import FJAIVideoMultiModeImageUploaderContainer from '../container/FJAIVideoMultiModeImageUploaderContainer.jsx';
import FJTextAreaWidthInspire from '../../../../common/combineComponents/FJTextAreaWidthInspire/FJTextAreaWidthInspire.jsx';
import FJButtonChoose from '../../../../common/combineComponents/FJButtonChoose/FJButtonChoose.jsx';
import FJRatioChoose from '../../../../common/combineComponents/FJRatioChoose/FJRatioChoose.jsx';
import FJLabeledSwitch from '../../../../common/components/FJLabeledSwitch/FJLabeledSwitch.jsx';
import FJGenerateButton from '../../../../common/components/FJGenerateButton/FJGenerateButton.jsx';
import classNames from 'classnames';
import FJTitleLine from '../../../../common/combineComponents/FJTitleLine/FJTitleLine.jsx';

import styles from './FJAITextOrImageToVideo.module.less';
import FJModelSelectorPanel from '../../../../common/components/FJModelSelectorPanel/FJModelSelectorPanel.jsx';
import FJUtil from '../../../../util/FJUtil.js';
import FJGeneratePrivacySettingsContainer from '../container/FJGeneratePrivacySettingsContainer.jsx';
import FJSwitchButton from '../../../../common/components/FJSwitchButton/FJSwitchButton.jsx';
import FJOrientationChoose from '../../../../common/combineComponents/FJOrientationChoose/FJOrientationChoose.jsx';
import FJMotionControlPresetList from './FJMotionControlPresetList.jsx';
const FJAITextOrImageToVideo = props => {
    const {
        currentRatio,
        changeRatio,
        onChangeDuration,
        currentDuration,
        currentResolution,
        onChangeResolution,
        tabIndex,
        textValue,
        onTextValueChange,
        getInspire,
        totalCredit,
        audioActive,
        onChangeAudioActive,
        currentModel,
        onGenerate,
        taskStatus,
        freeNumCreation,
        isProjectLoading,
        creditNumber,
        defaultFreeNumCreation,
        onChangeModel,
        imageMode,
        onChangeCharacterOrientation,
        curCharacterOrientation,
        onMotionControlPresetClick,
        motionControlTextSwitch,
        setMotionControlTextSwitch,
    } = props;
    const isFree = freeNumCreation > 0 && creditNumber < 10;

    // 根据当前模型获取配置
    const currentModelConfig = useMemo(() => {
        return AI_VIDEO_GENERATION_MODEL_LIST.find(model => model.value === currentModel);
    }, [currentModel]);
    const isMotionControl = useMemo(() => {
        return currentModel === MODEL_VALUE_MAP.KLING_V26_MOTION_CONTROL;
    }, [currentModel]);

    // 根据当前模型获取比例列表
    const ratioList = useMemo(() => {
        if (currentModelConfig?.ratios) {
            return currentModelConfig.ratios || [];
        }
        return FJAIVideoGeneratorData.RATIO_LIST;
    }, [currentModelConfig]);
    const resolutionList = useMemo(() => {
        if (currentModelConfig?.resolutions) {
            return currentModelConfig.resolutions;
        }
        return null;
    }, [currentModelConfig]);
    // 根据当前模型获取时长列表
    const durationList = useMemo(() => {
        if (currentModelConfig?.customDurations) {
            return null;
        }
        let list = currentModelConfig?.durations || FJAIVideoGeneratorData.DURATION_LIST;
        return list.map(item => {
            let disable = false;
            if (currentModelConfig?.startEndSupportDurations && imageMode === IMAGE_UPLOAD_MODES.START_END) {
                disable = !currentModelConfig?.startEndSupportDurations.includes(item.value);
            }
            if (resolutionList) {
                const currentResolutionItem = resolutionList.find(item => item.value === currentResolution);
                if (currentResolutionItem?.supportDurations) {
                    disable = !currentResolutionItem.supportDurations.includes(item.value);
                }
            }
            return { ...item, disable };
        });
    }, [currentModelConfig, resolutionList, currentResolution, imageMode]);
    // 根据当前模型判断是否显示比例选择器
    const shouldShowRatio = useMemo(() => {
        return (
            currentModelConfig?.ratios &&
            currentModelConfig.ratios.length > 0 &&
            (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO ||
                currentModelConfig.supportImageSelectRatio ||
                currentModelConfig.mode === IMAGE_UPLOAD_MODES.MULTI) // REQUIREMENT: google-veo-2和多图模式需要显示比例选择器
        );
    }, [currentModelConfig, tabIndex]);
    const maxPromptLength = useMemo(() => {
        return currentModelConfig?.promptCharacterLimit || 50000;
    }, [currentModelConfig]);
    const isWarningPromptLength = useMemo(() => {
        return textValue.length > maxPromptLength;
    }, [textValue, maxPromptLength]);
    const dropdownModelList = useMemo(() => {
        const orderList =
            tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO
                ? TEXT_TO_VIDEO_DROPDOWN_LIST
                : IMAGE_TO_VIDEO_DROPDOWN_LIST;

        const list = orderList.map(model => AI_VIDEO_GENERATION_MODEL_LIST.find(item => item.value === model));
        return list
            .filter(model => model.supportedModes.includes(tabIndex))
            .map(model => {
                const duration = model.customDurations ? model.defaultMinDuration : model.durations[0]?.value;

                const useCredit = model.creditPrice * duration;
                const modelWithCredit = {
                    ...model,
                    useCredit,
                };
                return model.value === MODEL_VALUE_MAP.KLING_16_PRO
                    ? { ...modelWithCredit, description: '1080p-output-with-refined-motion-logic-and-effects' }
                    : modelWithCredit;
            });
    }, [tabIndex]);

    // 根据当前模型判断是否显示音效开关
    const shouldShowSfx = useMemo(() => {
        return currentModelConfig?.sfxSupported !== false;
    }, [currentModelConfig]);

    // 根据当前模型判断prompt是否必填
    const isPromptRequired = useMemo(() => {
        return currentModelConfig?.promptRequired === true || tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO;
    }, [currentModelConfig, tabIndex]);

    const placeholder = () => {
        if (currentModel === MODEL_VALUE_MAP.MINIMAX_01_DIRECTOR) {
            return FJLocalStore._('camera-movement-instructions-can-be-added-using-square-brackets');
        }
        return isPromptRequired
            ? FJLocalStore._('describe-the-video-you-want-to-generate')
            : FJLocalStore._('describe-the-video-you-want-to-generate-optional');
    };
    const renderModelSelector = () => {
        const maxHeight = FJUtil.isAiVideoGeneratorLandingPage() ? '810px' : '';
        return (
            <FJModelSelectorPanel
                maxHeight={maxHeight}
                currentModelValue={currentModel}
                onSelect={onChangeModel}
                modelList={dropdownModelList}
                currentMode={
                    tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO ? 'ai-text-to-video' : 'ai-image-to-video'
                }
                filterConfig={
                    tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO
                        ? TEXT_TO_VIDEO_MODEL_FILTERS
                        : IMAGE_TO_VIDEO_MODEL_FILTERS
                }
                offsetLeft={20}
                offsetTop={-21}
            />
        );
    };
    return (
        <FJAppLeft style={{ paddingRight: '6px', paddingBottom: '20px' }}>
            <FJVerticalScroller className={styles.scroller}>
                <div style={{ paddingRight: '5px', paddingBottom: '20px' }}>
                    <div className={styles.model_picker_box}>{renderModelSelector()}</div>
                    {tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO && (
                        <div className={styles.image_upload_box}>
                            <FJAIVideoMultiModeImageUploaderContainer />
                        </div>
                    )}
                    {isMotionControl && <FJMotionControlPresetList onItemClick={onMotionControlPresetClick} />}
                    <FJTitleLine
                        title={FJLocalStore._('prompt')}
                        isShowTry={false}
                        isShowHelp={false}
                        className={classNames(styles.title_line, isPromptRequired ? styles.title_require : null)}
                        style={{
                            marginTop: tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO ? '21px' : '0px',
                            marginBottom: !motionControlTextSwitch && isMotionControl ? 0 : '8px',
                        }}
                        isRequire={isPromptRequired}
                        renderRight={() => (
                            <div className={styles.prompt_right}>
                                <div
                                    className={classNames(
                                        styles.text_limit,
                                        isWarningPromptLength ? styles.text_limit_warning : null,
                                    )}
                                    style={{
                                        visibility: isMotionControl && !motionControlTextSwitch ? 'hidden' : 'visible',
                                    }}
                                >
                                    {textValue.length}/{maxPromptLength}
                                </div>
                                {isMotionControl && (
                                    <FJSwitchButton
                                        active={motionControlTextSwitch}
                                        onChange={() => setMotionControlTextSwitch(!motionControlTextSwitch)}
                                    />
                                )}
                            </div>
                        )}
                    />

                    <FJTextAreaWidthInspire
                        placeholder={placeholder()}
                        text={textValue}
                        onChange={onTextValueChange}
                        hasInspire={tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO}
                        maxLength={50000}
                        rows={isMotionControl ? 5 : 7}
                        getInspire={getInspire}
                        customBottomClassName={classNames(
                            tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO
                                ? styles.custom_bottom_box
                                : null,
                            isMotionControl ? styles.motion_control_bottom_box : null,
                        )}
                        className={
                            isMotionControl
                                ? !motionControlTextSwitch
                                    ? styles.motion_control_input_box_hide
                                    : styles.motion_control_input_box_show
                                : ''
                        }
                        inputClassName={isMotionControl && !motionControlTextSwitch && styles.motion_control_input_hide}
                    />

                    {shouldShowRatio && (
                        <FJRatioChoose
                            list={ratioList}
                            currentRatio={currentRatio}
                            changeRatio={changeRatio}
                            className={styles.ratio_choose}
                            buttonClassName={styles.ratio_choose_button}
                        />
                    )}
                    {currentModelConfig?.characterOrientation && (
                        <FJOrientationChoose
                            list={currentModelConfig?.characterOrientation}
                            value={curCharacterOrientation}
                            onChange={onChangeCharacterOrientation}
                        />
                    )}
                    {resolutionList && (
                        <FJButtonChoose
                            title={FJLocalStore._('resolution')}
                            list={resolutionList}
                            currentValue={currentResolution}
                            onChange={onChangeResolution}
                        />
                    )}
                    {durationList && (
                        <FJButtonChoose
                            title={FJLocalStore._('duration')}
                            list={durationList}
                            currentValue={currentDuration}
                            onChange={onChangeDuration}
                        />
                    )}
                    {shouldShowSfx && (
                        <FJLabeledSwitch
                            className={styles.sfx_switch}
                            title={currentModelConfig?.sfxTitle || FJLocalStore._('ai-sfx-1')}
                            onChange={onChangeAudioActive}
                            active={audioActive}
                            desc={FJLocalStore._('ai-sfx-switch-tips')}
                            hideDesc={currentModelConfig?.hideSfxDesc}
                        />
                    )}
                    <FJGeneratePrivacySettingsContainer />
                </div>
            </FJVerticalScroller>
            <div className={styles.footer}>
                <FJGenerateButton
                    onClick={onGenerate}
                    taskStatus={taskStatus}
                    freeNumCreation={freeNumCreation}
                    isFree={isFree}
                    useCredit={totalCredit}
                    isProjectLoading={isProjectLoading}
                    isShowUsageDetail={true}
                    defaultFreeNumCreation={defaultFreeNumCreation}
                />
            </div>
        </FJAppLeft>
    );
};
FJAITextOrImageToVideo.propTypes = {
    currentRatio: PropTypes.string,
    changeRatio: PropTypes.func,
    onChangeQuality: PropTypes.func,
    onChangeDuration: PropTypes.func,
    currentQuality: PropTypes.number,
    currentDuration: PropTypes.number,
    tabIndex: PropTypes.string,
    textValue: PropTypes.string,
    onTextValueChange: PropTypes.func,
    getInspire: PropTypes.func,
    onGenerate: PropTypes.func,
    taskStatus: PropTypes.string,
    freeNumCreation: PropTypes.number,
    creditNumber: PropTypes.number,
    peopleImageInfo: PropTypes.object,
    clothesImageInfo: PropTypes.object,
    onUploadImage: PropTypes.func,
    selectedStyle: PropTypes.string,
    onSelectStyle: PropTypes.func,
    isProjectLoading: PropTypes.bool,
    totalCredit: PropTypes.number,
    audioActive: PropTypes.bool,
    onChangeAudioActive: PropTypes.func,
    currentModel: PropTypes.string,
    defaultFreeNumCreation: PropTypes.number,
    onChangeModel: PropTypes.func,
    currentResolution: PropTypes.string,
    onChangeResolution: PropTypes.func,
    imageMode: PropTypes.string,
    onChangeCharacterOrientation: PropTypes.func,
    curCharacterOrientation: PropTypes.string,
};
export default FJAITextOrImageToVideo;
